import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { CostsService } from '../../api/CostsAPI';
import { LOCATION_META } from '../../constants';
import { closestWithClass } from '../../utils';
import s from '../../styles/dashboard.module.css';

const KINDS = [
  { id: 'contractor', label: 'Contractor' },
  { id: 'supply',     label: 'Supply' },
  { id: 'other',      label: 'Other' },
];

// Matches the camera on the appointment card, so the two read as one family.
function CameraGlyph() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

const toMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const parseMoney = (text) => {
  const n = parseFloat(String(text ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
const newLine = () => ({
  key: globalThis.crypto?.randomUUID?.() || `l-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  kind: 'contractor', payee: '', payeeName: '', amountText: '$0.00', note: '',
  invoiceFile: null, invoiceName: '', idInvoice: null,
});

// One cost line. Several can be open at once via "+ Add another cost".
function CostLine({ line, payees, onChange, onAddVendor, onPickInvoice, disabled }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const set = (patch) => onChange({ ...line, ...patch });

  // The menu floats in a portal on <body>. It has to: the modal scrolls with
  // `overflow-y: auto`, which clips any absolutely positioned child.
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 10;
      const above = r.top - 10;
      // Drop upward only when below is genuinely cramped and up has more room.
      const flip = below < 220 && above > below;
      setMenuPos({
        left: Math.round(r.left),
        width: Math.round(r.width),
        maxHeight: Math.round(Math.max(160, Math.min(320, flip ? above : below))),
        ...(flip
          ? { bottom: Math.round(window.innerHeight - r.top + 6) }
          : { top: Math.round(r.bottom + 6) }),
      });
    };
    place();
    // Capture phase, so scrolling the modal body moves the menu with it.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setPickerOpen(false);
    };
    // Capture Escape before the modal sees it, so the first press closes only
    // the menu.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [pickerOpen]);

  const [query, setQuery] = useState('');
  const match = (n) => n.toLowerCase().includes(query.trim().toLowerCase());
  const vendors = useMemo(() => (payees?.vendors || []).filter(v => match(v.name)), [payees, query]);
  const crew    = useMemo(() => (payees?.staff   || []).filter(v => match(v.name)), [payees, query]);
  const nothing = !vendors.length && !crew.length;

  const closePicker = () => { setPickerOpen(false); setQuery(''); };

  return (
    <div className={s.costLine}>
      <div className={s.drawerCardLabel}>Who is being paid</div>
      <div className={s.costKindRow}>
        {KINDS.map(k => (
          <button key={k.id} type="button" disabled={disabled}
                  className={`${s.costKind} ${line.kind === k.id ? s.costKindActive : ''}`}
                  onClick={() => set({ kind: k.id })}>
            {k.label}
          </button>
        ))}
      </div>

      <div className={s.payFieldRow}>
        <div className={s.payField} style={{ flex: 2 }}>
          <span className={s.drawerCardLabel}>Vendor</span>
          <button ref={triggerRef} type="button" className={s.costSelect} disabled={disabled}
                  onClick={() => setPickerOpen(o => !o)}>
            <span>{line.payeeName || 'Select a vendor'}</span>
            <svg className={s.costChevron} width="12" height="12" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" strokeWidth="2.5"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {pickerOpen && menuPos && createPortal(
            /* The portal lands outside the page's `.lightRoot`, so carry the
               class across or the menu keeps its dark palette in light mode. */
            <div className={closestWithClass(triggerRef.current, s.lightRoot) ? s.lightRoot : undefined}>
              <div ref={menuRef} className={s.costMenu} style={menuPos}>
                <input
                  className={s.costMenuSearch}
                  placeholder="Search vendors and crew"
                  value={query}
                  autoFocus
                  onChange={(e) => setQuery(e.target.value)}
                />
                {/* Scrolls within itself, so a long roster cannot push the rest
                    of the form off the screen. */}
                <div className={s.costMenuList}>
                  {vendors.length > 0 && <div className={s.costMenuGroup}>Vendors</div>}
                  {vendors.map(o => (
                    <button key={o.id} type="button" className={s.costMenuItem}
                            onClick={() => { set({ payee: o.id, payeeName: o.name }); closePicker(); }}>
                      {o.name}
                    </button>
                  ))}
                  {crew.length > 0 && <div className={s.costMenuGroup}>Crew</div>}
                  {crew.map(o => (
                    <button key={o.id} type="button" className={s.costMenuItem}
                            onClick={() => { set({ payee: o.id, payeeName: o.name }); closePicker(); }}>
                      {o.name}
                    </button>
                  ))}
                  {nothing && <div className={s.costMenuEmpty}>No match for “{query}”</div>}
                </div>
                <button type="button" className={`${s.costMenuItem} ${s.costMenuAdd}`}
                        onClick={async () => {
                          const name = window.prompt('New vendor name');
                          if (!name?.trim()) return;
                          const added = await onAddVendor(name.trim(), line.kind);
                          if (added) set({ payee: `vendor:${added.idVendor}`, payeeName: added.name });
                          closePicker();
                        }}>
                  + Add a vendor
                </button>
              </div>
            </div>,
            document.body,
          )}
        </div>

        <label className={s.payField}>
          <span className={s.drawerCardLabel}>Amount</span>
          <input className={s.payInput} value={line.amountText} disabled={disabled}
                 onChange={e => set({ amountText: e.target.value })}
                 onBlur={() => set({ amountText: toMoney(parseMoney(line.amountText)) })} />
        </label>
      </div>

      <div className={s.drawerCardLabel} style={{ marginTop: 14 }}>Invoice</div>
      <label className={s.costInvoice}>
        <input type="file" accept="image/*,application/pdf" capture="environment"
               disabled={disabled} style={{ display: 'none' }}
               onChange={(e) => {
                 const file = e.target.files?.[0];
                 e.target.value = '';
                 if (file) onPickInvoice(file);
               }} />
        <span className={s.costInvoiceIcon}><CameraGlyph /></span>
        <span>
          <span className={s.costInvoiceTitle}>
            {line.invoiceName || 'Take a photo of the invoice'}
          </span>
          <span className={s.costInvoiceSub}>
            {line.invoiceName ? 'Tap to replace' : 'Opens the camera, or choose a file'}
          </span>
        </span>
      </label>

      <div className={s.drawerCardLabel} style={{ marginTop: 14 }}>Note</div>
      <input className={s.payInput} placeholder="What the cost covers" value={line.note}
             disabled={disabled} onChange={e => set({ note: e.target.value })} />
    </div>
  );
}

export function LogCostModal({ appt, onClose, onSaved }) {
  const [lines, setLines] = useState([newLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const { data: payees, refetch } = useQuery({
    queryKey: ['cost-payees'],
    queryFn: () => CostsService.payees(),
    staleTime: 10 * 60 * 1000,
  });

  const totalCents = lines.reduce((n, l) => n + parseMoney(l.amountText), 0);
  const canSave = totalCents > 0 && !saving;

  const locName = LOCATION_META[appt?.location]?.name || appt?.location;

  const addVendor = async (name, kind) => {
    try {
      const res = await CostsService.addVendor(name, kind);
      await refetch();
      return res.vendor;
    } catch (err) {
      setError(err?.message || 'Could not add that vendor');
      return null;
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const bookId = appt.externalId || appt.id;
      const payable = lines.filter(l => parseMoney(l.amountText) > 0);

      // Upload first: the cost row stores the invoice's id, so the bill has to
      // exist before the cost that references it.
      const withInvoices = [];
      for (const l of payable) {
        let idInvoice = l.idInvoice;
        if (l.invoiceFile && !idInvoice) {
          const res = await CostsService.uploadInvoice(bookId, l.invoiceFile);
          idInvoice = res?.invoice?.idInvoice ?? null;
        }
        withInvoices.push({
          kind: l.kind, payee: l.payee || undefined, payeeName: l.payeeName || undefined,
          amount: parseMoney(l.amountText), note: l.note || undefined,
          idInvoice: idInvoice || undefined, idempotencyKey: l.key,
        });
      }
      await CostsService.log(bookId, withInvoices);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not log the cost');
      setSaving(false);
    }
  };

  return (
    <div className={s.qrOverlay} onClick={saving ? undefined : onClose}>
      <div className={s.costModal} onClick={e => e.stopPropagation()}>
        <div className={s.drawerHead}>
          <div>
            <div className={s.drawerEyebrow}>Log a cost</div>
            <div className={s.drawerTitle}>{appt?.customer}</div>
            <div className={s.drawerSub}>{[appt?.time, locName].filter(Boolean).join(' · ')}</div>
          </div>
          <button className={s.drawerClose} onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>

        {lines.map((line, i) => (
          <CostLine key={line.key} line={line} payees={payees} disabled={saving}
                    onAddVendor={addVendor}
                    onPickInvoice={(file) => setLines(ls => ls.map((l, j) => (
                      j === i ? { ...l, invoiceFile: file, invoiceName: file.name } : l)))}
                    onChange={(next) => setLines(ls => ls.map((l, j) => (j === i ? next : l)))} />
        ))}

        <button type="button" className={s.costAddLine} disabled={saving}
                onClick={() => setLines(ls => [...ls, newLine()])}>
          + Add another cost
        </button>

        {error && <div className={s.nextStepError}>{error}</div>}

        <div className={s.costFooter}>
          <div>
            <div className={s.drawerCardLabel}>Total cost</div>
            <div className={s.drawerTitle} style={{ fontSize: 17, margin: 0 }}>{toMoney(totalCents)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className={s.drawerSecondary} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className={s.drawerPrimary} onClick={save} disabled={!canSave}>
              {saving ? 'Saving…' : 'Log cost'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
